/**
 * packages/ui/seeAlsoLabel.mjs — RUN SH · SH-3. THE NEVER-BARE RULE, IN ONE FUNCTION, ONCE.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ADAM'S RULE, 2026-08-25: **name only, or the cite explicitly attributed to the answer. Never
 * bare, not even in a tooltip.** Every row a See-also group holds is an authority the corpus did
 * not confirm, so its reporter citation was produced by the MODEL and was never read out of the
 * corpus. RUN PJ measured what happens when a surface prints it anyway: the callout read
 * *"Caron v. United States 524 U.S. 308"* — name, space, volume, page, no comma, no year — inside a
 * block whose own sentence said we could not verify it.
 * ⭐ A fabricated reporter cite rendered as a citation is the worst artifact this product can make.
 *
 * THREE SHAPES AND NO FOURTH:
 *   name present            → the NAME, alone. The cite is not printed at all, and not put in a
 *                             `title` either — a tooltip is a place a citation can be copied out of.
 *   no name, cite present   → one sentence that says WHOSE citation it is before it says what it
 *                             is. The cite is still shown because it is the only handle the reader
 *                             has (RUN PJ's `nameless` arm), and it is shown ATTRIBUTED.
 *   neither                 → words, because a blank row is indistinguishable from a broken one.
 *
 * ══ 🔴🔴 THE TWO COPIES HAD ALREADY DRIFTED, AND THE DRIFT WAS A NEVER-BARE VIOLATION. ══════════
 *
 * This function was module-private in `apps/main/.../AuthorityCard.jsx` (RUN VC) and again in
 * `apps/steel/.../SeeAlsoCards.jsx` (RUN RC). SH-3's brief describes them as "duplicated the same
 * way". **They were not the same.** Measured before the swap, both sliced out of the files on disk
 * and run over 270 rows — every `stripped` and `unchecked` row in the Clark and Research archives,
 * in both the raw-envelope and rail-mapped shapes, plus 16 adversarial rows:
 *
 *     Clark  `const asserted = s.citeSource === 'model_asserted';`
 *     Steel  `const asserted = (s.citeSource ?? 'model_asserted') === 'model_asserted';`
 *     ⇒ **79 of 270 disagree**, and every one of them is a row with a cite, NO name, and NO
 *       `citeSource` field at all. 75 are REAL ARCHIVED ROWS (74 research statutes such as
 *       `18 U.S.C. § 2703(d)` and `Fed. R. Civ. P. 56`; one Clark row,
 *       `Restatement (Second) of Contracts § 90`).
 *
 * On those rows Clark's copy falls past the attributed branch into `return { text: s.cite }` —
 * **the bare citation** — and steel's does not. `apps/main/ops/run-sh/out/agree-before.json`.
 *
 * ⭐ THE CHOICE BETWEEN THEM IS FORCED, NOT A PREFERENCE. Extracting Clark's version would have put
 * 74 bare statute cites onto Research's live surface. Extracting steel's changes NOTHING on either
 * surface today, because the disagreement lives only in the shape Clark never sees: `railPayload.js`
 * already writes `citeSource: s.citeSource ?? 'model_asserted'` upstream, and **0 of the 135
 * rail-mapped rows disagree.** So Clark was correct on screen by an upstream default in a different
 * file, and wrong in the function that exists to be the guard. This file restores the guard.
 *
 * 🔴 THE GUARD TESTS FOR THE POSITIVE VALUE — `=== 'model_asserted'`, never `!== 'corpus'`. A
 * producer that starts sending a third value must fall into the SAFE branch, and only a positive
 * test does that. The `??` does not weaken it: an ABSENT value is read as model-asserted, which is
 * the conservative reading for a group whose whole premise is that the corpus did not supply these.
 *
 * ══ 🔴🔴 RUN AC · BLOCK 6 — A STATUTE'S CITATION **IS** ITS NAME. FOURTH SHAPE, NOT AN
 * EXCEPTION TO THE RULE. ═══════════════════════════════════════════════════════
 *
 * WHAT ADAM SAW, and RUN AC reproduced verbatim on the live surface at 1440
 * (ops/run-ac/out/seealso-before.json, question *"is self help evictions legal"*):
 *
 *     Case      Berkeley Community Health Project, Inc. v. City of Berkeley
 *     Statute   The answer gave only a citation, “Cal. Code Civ. Proc. § 1159”, which we could
 *               not confirm.
 *     Statute   The answer gave only a citation, “Cal. Civ. Code § 789.3”, which we could not
 *               confirm.
 *
 * ⭐ THE TWO BRANCHES DIVERGE ON `s.name`, NOT ON `s.kind`. A case row arrives with a name and
 * takes the first branch; a statute row arrives `name: null` with only a cite and falls to the
 * attributed branch. So the card was never the problem and neither was the chip — the case path
 * was already right, and the statute path had no branch of its own to be right in.
 *
 * 🔴 WHY THIS IS NOT A HOLE IN THE NEVER-BARE RULE. That rule protects against a REPORTER
 * CITATION — volume, reporter, page — standing in for a case the corpus never confirmed: a
 * pinpoint locator a lawyer can paste into a brief, which RUN PJ measured rendering as
 * *"Caron v. United States 524 U.S. 308"*. A statute citation is not a locator into a reporter.
 * `Cal. Code Civ. Proc. § 1159` is the section's NAME — the only name it has, the string a reader
 * would look it up by, and the thing the sentence was withholding from them. Printing it is the
 * same act as printing a case name, which this function has always done in the first branch.
 * ⭐ The disclosure that was lost is not lost: the group is headed SEE ALSO, the card sits on the
 * grey ground with no click target, and RUN AB set that treatment *precisely so* these read as
 * "named, not held". The sentence was the same fact told twice, and the second telling cost the
 * reader the citation.
 *
 * ⚠️ GATED ON `kind === 'statute'`, WHICH IS THE PRODUCER'S OWN EXPLICIT VALUE and is deliberately
 * narrow. `clarkAnswerV2.js` maps `kind: s.kind === 'statute' ? 'statute' : 'case'`, so anything
 * NOT explicitly a statute — a rule, a restatement, a treatise, an unlabelled row — keeps the
 * attributed sentence. A row that only MIGHT be a statute is exactly the row the conservative
 * branch is for. (That same mapping is why `735 ILCS 5` can wear a **Case** chip; observed by RUN
 * AB, reported again by RUN AC, and not fixed here — it is a producer defect in a different file.)
 *
 * ══ 🔴🔴 RUN RQ · §1 — THE `attributed` SHAPE IS DELETED. THE CITATION IS THE NAME, FOR EVERY
 * ROW, NOT ONLY FOR A STATUTE. ═══════════════════════════════════════════════════════════════
 *
 * WHAT ADAM READ IN THE SEE ALSO PANEL, and ruled on:
 *
 *     The answer gave only a citation, “Fed. R. Civ. P. 56”, which we could not confirm.
 *
 * *"That reads as incompetence and it goes. Render the citation plainly and confidently — bold,
 * like the other citations, but in BLACK so it is visibly not clickable. No apology, no hedge, no
 * sentence about what we could not do."*
 *
 * ⭐ RUN AC HAD ALREADY BUILT THE RENDERING HE IS ASKING FOR. Shape `cite` sets the citation in the
 * NAME's face — same size, same weight, same `--cc-ink`, roman rather than italic because Indigo
 * R2.1 reserves italic for case names — on the grey `--cc-bg-subtle` ground RUN AB gave this group,
 * with no anchor and no hover. MEASURED, `--cc-ink` on `--cc-bg-subtle`: **16.99:1 light, 15.96:1
 * dark, 14.17:1 gavel** — above AAA in all three, and higher than both the accent a resolved name
 * wears (9.69 / 8.71 / 5.28) and the muted sentence face the apology wore (6.93 / 8.98 / 6.97). So
 * the citation we could NOT confirm is now the most legible thing on the card, which is the point:
 * it is what the reader has to go and check.
 *
 * 🔴 ALL THAT WAS EVER WRONG WAS THE GATE. AC wrote `s.kind === 'statute' && s.cite`, deliberately
 * narrow, so *"a rule, a restatement, a treatise, an unlabelled row"* kept the sentence. That gate
 * never opened for `Fed. R. Civ. P. 56`: the model's ranked list called it a CASE, and RUN RQ
 * measured the whole chain from that one field — `partitionCourtRules` only considers
 * `kind === 'statute'` so RUN VF's drop never fired, `resolveMention` sent it down the CASE
 * resolver and got `neither_path_resolved`, `clarkAnswerV2.js`'s two-valued fold kept `case`, the
 * rail drew a **Case** pill, and this function fell to `attributed`. One field, four wrong outputs.
 *
 * 🔴 AC's ARGUMENT NEVER DEPENDED ON THE ROW BEING A STATUTE, AND THAT IS WHY THE GATE COMES OFF
 * RATHER THAN WIDENING BY ONE MORE `kind`. The never-bare rule (SH-3, above) protects against a
 * REPORTER CITATION — volume, reporter, page — standing in for a case: a pinpoint locator a lawyer
 * can paste into a brief, which RUN PJ measured rendering as *"Caron v. United States 524 U.S.
 * 308"*. `Fed. R. Civ. P. 56` is not a locator into a reporter. Neither is `29 C.F.R. Part 541`,
 * nor `Restatement (Second) of Contracts § 90` — the three real populations SH-3's own measurement
 * names. Each IS the authority's name: the only name it has, and the string a reader would look it
 * up by. Printing it is the same act as printing a case name, which branch one has always done.
 *
 * ⚠️ AND PJ's HAZARD IS STILL COVERED, BY THE BRANCH ABOVE THIS ONE. Caron had a NAME; it took
 * branch one and the bare volume-and-page beside it came from `AuthorityCard`'s meta line, not from
 * here. A row with a bare reporter cite and NO name would now print that cite — measured across the
 * 270 archived rows SH-3 enumerated, that population is EMPTY, and were it ever to appear it would
 * print one string the model asserted rather than a sentence apologising for it, on a card that
 * cannot be clicked, under a heading that says SEE ALSO.
 *
 * 🔴 THE DISCLOSURE IS NOT LOST — IT MOVED OUT OF THE PROSE AND INTO `.cc-sr-only`. The sentence
 * was the only thing telling a screen-reader user that this row is unconfirmed, so every call site
 * now renders that disclosure as clipped text plus a `title` tooltip, in the two voices RUN SH-1
 * fought for: `stripped` = we looked and it is not there; `unchecked` = our lookup did not finish.
 * Deleting the sentence without that would have taken the fact away from the readers who most need
 * it, which is not what Adam ruled on.
 *
 * 🔴 AND THE SHAPE IS RETURNED NOW INSTEAD OF BEING RE-DERIVED BY EVERY CALLER. All three call
 * sites carried the identical line `s.name ? 'name' : label.cited ? 'attributed' : 'words'` — a
 * fourth copy of this function's own branch table, in three files, which cannot learn about a
 * fourth shape. The shape a row took is this function's knowledge; it says so.
 *
 * @param {{name?: string|null, cite?: string|null, citeSource?: string|null, kind?: string|null}} s
 * @returns {{text: string, cited: null, shape: 'name'|'cite'|'words'}}
 *          `cited` is ALWAYS null now and is kept only so a caller that still branches on it takes
 *          the plain path rather than crashing on an absent key; it may be deleted once every call
 *          site has dropped its branch. `shape` tells the caller which face to set `text` in:
 *          `name` and `cite` are authority NAMES (`name` italic per Indigo R2.1, `cite` roman);
 *          `words` is the one remaining sentence the product speaks in its own voice, and it is
 *          reached only when there is neither a name nor a cite to print.
 */
export function seeAlsoLabel(s) {
  if (s.name) return { text: s.name, cited: null, shape: 'name' };
  if (s.cite) return { text: s.cite, cited: null, shape: 'cite' };
  return { text: 'An authority this answer named could not be identified.', cited: null, shape: 'words' };
}

export default seeAlsoLabel;
